import React, { useState, useContext } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import AppContext from '../../../context/AppContext';
// ✅ Reusing the exact classes from ContactPage.css (form + card + toast +
// button styling) so this looks identical, just without the info/map side.
import "../../../assets/css/gcstudent/contactUs/ContactPage.css";
// ✅ Reusing the same title/subtitle convention as the other homepage
// sections (Featured Books/PDFs/Notes) for a consistent look.
import "../../../assets/css/gcstudent/featuredbooksection/FeaturedBooksSection.css";

// ── SVG Icon Components (same as ContactPage) ──────────────
const IconUser = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconAt = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-3.92 7.94" />
  </svg>
);

const IconTag = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const IconMessage = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
);

const IconSend = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconAlert = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ── Get In Touch — standalone contact form for the Home page ──
const GetInTouchSection = () => {
  const { baseUrl } = useContext(AppContext);

  const [formData, setFormData] = useState({
    fullName: '', email: '', subject: '', message: '',
  });

  // status: null | 'loading' | 'success' | 'error'
  const [status, setStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (status) setStatus(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { fullName, email, subject, message } = formData;

    if (!fullName || !email || !subject || !message) {
      setStatus('error');
      setStatusMessage('Please fill in all fields before sending.');
      return;
    }

    setStatus('loading');

    axios.post(`${baseUrl}/contact/sendContactMessage`, {
      name: fullName,
      email: email,
      subject: subject,
      message: message,
    })
      .then((res) => {
        setStatus('success');
        setStatusMessage(res.data.message || "Your message has been sent successfully! We'll get back to you soon.");
        setFormData({ fullName: '', email: '', subject: '', message: '' });
      })
      .catch((err) => {
        setStatus('error');
        setStatusMessage(err.response?.data?.message || 'Something went wrong. Please try again.');
      });
  };

  return (
    <section className="gcStudentContactPage">
      <Container>

        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <h2 className="gcStudent-featured-title" style={{ marginBottom: "8px" }}>
            Get In Touch
          </h2>
          <p className="gcStudent-featured-subtitle" style={{ margin: 0 }}>
            Have questions? We'd love to hear from you.
          </p>
        </div>

        {/* ── Centered Form Card ── */}
        <Row className="justify-content-center" style={{ justifyContent: "center" }}>
          <Col lg={7}>
            <div className="gcStudentContactCard">
              <div className="gcStudentFormSection">
                <form onSubmit={handleSubmit} noValidate>

                  {/* Full Name */}
                  <div className="gcStudentFormGroup">
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Full Name"
                      className="gcStudentFormControl"
                      autoComplete="name"
                    />
                    <span className="gcStudentInputIcon"><IconUser /></span>
                  </div>

                  {/* Email */}
                  <div className="gcStudentFormGroup">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email Address"
                      className="gcStudentFormControl"
                      autoComplete="email"
                    />
                    <span className="gcStudentInputIcon"><IconAt /></span>
                  </div>

                  {/* Subject */}
                  <div className="gcStudentFormGroup">
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Subject"
                      className="gcStudentFormControl"
                    />
                    <span className="gcStudentInputIcon"><IconTag /></span>
                  </div>

                  {/* Message */}
                  <div className="gcStudentFormGroup gcStudentTextareaWrapper">
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Your Message"
                      className="gcStudentFormControl"
                      rows={5}
                    />
                    <span className="gcStudentInputIcon"><IconMessage /></span>
                  </div>

                  {/* Status Toast */}
                  {status === 'success' && (
                    <div className="gcStudentToast success">
                      <IconCheck />
                      {statusMessage}
                    </div>
                  )}
                  {status === 'error' && (
                    <div className="gcStudentToast error">
                      <IconAlert />
                      {statusMessage}
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    className="gcStudentSendBtn"
                    disabled={status === 'loading'}
                  >
                    {status === 'loading' ? (
                      <>
                        <div className="gcStudentSpinner" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <IconSend />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </Col>
        </Row>

      </Container>
    </section>
  );
};

export default GetInTouchSection;